import { useEffect, useState, useRef } from "react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import api, { formatError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, DollarSign, Upload, CreditCard, CheckCircle2, Clock, XCircle, ExternalLink, FileUp } from "lucide-react";

const PAYNOW_IMG = "https://customer-assets-cm19k8pv.emergentagent.net/job_accommodation-hub-53/artifacts/7cn9gh89_Screenshot%202026-09-29%20121837.png";

const statusColors = {
  pending: "bg-amber-50 text-amber-700",
  paid: "bg-emerald-50 text-emerald-700",
  failed: "bg-red-50 text-red-700",
  reversed: "bg-gray-100 text-gray-500",
  under_review: "bg-blue-50 text-blue-700",
  awaiting_payment: "bg-violet-50 text-violet-700",
};

const statusIcons = {
  pending: Clock,
  paid: CheckCircle2,
  failed: XCircle,
  under_review: Clock,
  awaiting_payment: CreditCard,
};

export default function TenantPaymentsPage() {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [payDialog, setPayDialog] = useState(null);
  const [proofDialog, setProofDialog] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const fetchPayments = () => {
    api.get("/payments")
      .then((r) => setPayments(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchPayments(); }, []);

  const totalPaid = payments.filter((p) => p.status === "paid").reduce((s, p) => s + p.amount, 0);
  const totalPending = payments.filter((p) => ["pending", "awaiting_payment"].includes(p.status)).reduce((s, p) => s + p.amount, 0);
  const pendingPayments = payments.filter((p) => ["pending", "awaiting_payment"].includes(p.status));

  const handlePaynow = async (payment) => {
    setProcessing(true);
    try {
      const { data } = await api.post("/payments/paynow/create", { payment_id: payment.id || payment._id }, { timeout: 20000 });
      localStorage.setItem("lh_pending_payment_id", payment.id || payment._id);
      if (data.redirect_url) {
        window.location.href = data.redirect_url;
      } else {
        toast.error("No redirect URL received from Paynow");
      }
    } catch (err) {
      const status = err.response?.status;
      if (status === 504 || err.code === "ECONNABORTED") {
        toast.error("Payment gateway timed out. Please try again in a moment.");
      } else if (!err.response || err.message === "Network Error") {
        toast.error("Connection error. Please check your internet and try again.");
      } else {
        toast.error(formatError(err.response?.data?.detail) || "Payment failed. Please try again.");
      }
    } finally {
      setProcessing(false);
    }
  };

  const handleProofUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !proofDialog) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      await api.post(`/payments/proof-of-payment?payment_id=${proofDialog.id || proofDialog._id}`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Proof of payment uploaded! It will be reviewed by our finance team.");
      setProofDialog(null);
      fetchPayments();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const canPay = (p) => ["pending", "awaiting_payment"].includes(p.status);

  return (
    <DashboardLayout title="Payments">
      <div className="space-y-6" data-testid="tenant-payments-page">
        {/* Summary Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Card className="border-slate-200">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between mb-1">
                <p className="text-sm text-slate-500">Total Paid</p>
                <CheckCircle2 className="h-4 w-4 text-emerald-500" />
              </div>
              <p className="font-heading text-2xl font-bold text-emerald-700" data-testid="total-paid">${totalPaid.toFixed(2)}</p>
            </CardContent>
          </Card>
          <Card className="border-slate-200">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between mb-1">
                <p className="text-sm text-slate-500">Outstanding</p>
                <Clock className="h-4 w-4 text-amber-500" />
              </div>
              <p className="font-heading text-2xl font-bold text-amber-700" data-testid="total-pending">${totalPending.toFixed(2)}</p>
            </CardContent>
          </Card>
          <Card className="border-slate-200">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between mb-1">
                <p className="text-sm text-slate-500">Invoices</p>
                <DollarSign className="h-4 w-4 text-sky-500" />
              </div>
              <p className="font-heading text-2xl font-bold text-slate-900" data-testid="total-invoices">{payments.length}</p>
            </CardContent>
          </Card>
        </div>

        {/* Pending Payments — Pay Now Section */}
        {pendingPayments.length > 0 && (
          <Card className="border-sky-200 bg-sky-50/30" data-testid="pending-payments-section">
            <CardHeader>
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <CreditCard className="h-5 w-5 text-sky-600" /> Outstanding Payments
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {pendingPayments.map((p) => (
                <div
                  key={p.id || p._id}
                  className="bg-white rounded-xl border border-slate-200 p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4"
                  data-testid={`pending-payment-${p.id || p._id}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <Badge variant="outline" className="text-[11px] capitalize">{p.type}</Badge>
                      <span className="font-mono text-xs text-slate-400">{p.reference}</span>
                    </div>
                    <p className="font-heading text-xl font-bold text-slate-900">${p.amount.toFixed(2)}</p>
                    {p.admin_notes && <p className="text-xs text-slate-500 mt-0.5 truncate">{p.admin_notes}</p>}
                  </div>

                  <div className="flex flex-col items-center gap-2 w-full sm:w-auto">
                    {/* Paynow Button */}
                    <button
                      onClick={() => setPayDialog(p)}
                      className="group relative w-full sm:w-[200px] overflow-hidden rounded-xl transition-all hover:shadow-lg hover:shadow-sky-200/50 active:scale-[0.98]"
                      data-testid={`paynow-btn-${p.id || p._id}`}
                    >
                      <img src={PAYNOW_IMG} alt="Pay Now with Paynow" className="w-full h-auto rounded-xl" />
                    </button>
                    {/* Upload Proof Option */}
                    <button
                      onClick={() => setProofDialog(p)}
                      className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-sky-600 transition-colors"
                      data-testid={`upload-proof-btn-${p.id || p._id}`}
                    >
                      <Upload className="h-3 w-3" /> Upload proof of payment instead
                    </button>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Payment History */}
        <Card className="border-slate-200">
          <CardHeader><CardTitle className="font-heading text-lg">Payment History</CardTitle></CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
            ) : payments.length === 0 ? (
              <div className="text-center py-12 text-slate-400">No payments yet.</div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead className="text-xs font-bold uppercase">Reference</TableHead>
                      <TableHead className="text-xs font-bold uppercase">Type</TableHead>
                      <TableHead className="text-xs font-bold uppercase">Amount</TableHead>
                      <TableHead className="text-xs font-bold uppercase">Method</TableHead>
                      <TableHead className="text-xs font-bold uppercase">Status</TableHead>
                      <TableHead className="text-xs font-bold uppercase">Date</TableHead>
                      <TableHead className="text-xs font-bold uppercase text-right">Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {payments.map((p) => {
                      const SIcon = statusIcons[p.status] || Clock;
                      return (
                        <TableRow key={p.id || p._id} data-testid={`payment-row-${p.id || p._id}`}>
                          <TableCell className="font-mono text-xs">{p.reference}</TableCell>
                          <TableCell className="capitalize text-sm">{p.type}</TableCell>
                          <TableCell className="font-mono font-bold text-sm">${p.amount.toFixed(2)}</TableCell>
                          <TableCell className="text-xs text-slate-500 capitalize">{p.payment_method || "—"}</TableCell>
                          <TableCell>
                            <Badge className={`${statusColors[p.status] || "bg-slate-100"} inline-flex items-center gap-1`}>
                              <SIcon className="h-3 w-3" /> {p.status?.replace(/_/g, " ")}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-xs text-slate-400">{new Date(p.created_at).toLocaleDateString()}</TableCell>
                          <TableCell className="text-right">
                            {canPay(p) && (
                              <div className="flex items-center gap-1 justify-end">
                                <Button size="sm" variant="default" className="h-7 text-xs bg-sky-500 hover:bg-sky-600" onClick={() => setPayDialog(p)} data-testid={`pay-row-${p.id || p._id}`}>
                                  <CreditCard className="h-3 w-3 mr-1" /> Pay
                                </Button>
                                <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setProofDialog(p)} data-testid={`proof-row-${p.id || p._id}`}>
                                  <FileUp className="h-3 w-3 mr-1" /> Proof
                                </Button>
                              </div>
                            )}
                            {p.proof_of_payment_url && (
                              <a href={p.proof_of_payment_url} target="_blank" rel="noopener noreferrer" className="text-xs text-sky-600 hover:underline inline-flex items-center gap-1">
                                <ExternalLink className="h-3 w-3" /> View Proof
                              </a>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Paynow Payment Dialog */}
        <Dialog open={!!payDialog} onOpenChange={(o) => !o && !processing && setPayDialog(null)}>
          <DialogContent className="sm:max-w-md" data-testid="paynow-dialog">
            <DialogHeader>
              <DialogTitle className="font-heading">Pay with Paynow</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div className="bg-slate-50 rounded-lg p-4 space-y-2">
                <div className="flex justify-between text-sm"><span className="text-slate-500">Reference</span><span className="font-mono font-medium">{payDialog?.reference}</span></div>
                <div className="flex justify-between text-sm"><span className="text-slate-500">Type</span><span className="capitalize font-medium">{payDialog?.type}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Amount</span><span className="font-heading text-xl font-bold text-slate-900">${payDialog?.amount?.toFixed(2)}</span></div>
              </div>

              {/* Paynow Image */}
              <div className="flex justify-center">
                <img src={PAYNOW_IMG} alt="Paynow" className="w-[220px] h-auto rounded-lg" />
              </div>

              {/* Glass Effect Pay Button */}
              <button
                onClick={() => payDialog && handlePaynow(payDialog)}
                disabled={processing}
                className="w-full relative overflow-hidden rounded-xl py-3.5 px-6 font-heading font-bold text-lg text-white
                  bg-gradient-to-r from-sky-500 to-blue-600
                  backdrop-blur-sm border border-white/20
                  shadow-[inset_0_1px_0_0_rgba(255,255,255,0.2),0_4px_16px_rgba(14,165,233,0.3)]
                  hover:shadow-[inset_0_1px_0_0_rgba(255,255,255,0.3),0_6px_24px_rgba(14,165,233,0.4)]
                  hover:from-sky-400 hover:to-blue-500
                  active:scale-[0.98] transition-all duration-200
                  disabled:opacity-60 disabled:cursor-not-allowed"
                data-testid="confirm-paynow-btn"
              >
                <div className="absolute inset-0 bg-white/10 backdrop-blur-[2px] rounded-xl" />
                <span className="relative flex items-center justify-center gap-2">
                  {processing ? (
                    <><Loader2 className="h-5 w-5 animate-spin" /> Processing...</>
                  ) : (
                    <><CreditCard className="h-5 w-5" /> Pay ${payDialog?.amount?.toFixed(2)} Now</>
                  )}
                </span>
              </button>

              <p className="text-[11px] text-slate-400 text-center leading-relaxed">
                You'll be redirected to Paynow to complete payment via EcoCash, VISA, Mastercard, ZimSwitch, or Telecash.
              </p>
            </div>
          </DialogContent>
        </Dialog>

        {/* Proof of Payment Upload Dialog */}
        <Dialog open={!!proofDialog} onOpenChange={(o) => !o && !uploading && setProofDialog(null)}>
          <DialogContent className="sm:max-w-md" data-testid="proof-dialog">
            <DialogHeader>
              <DialogTitle className="font-heading">Upload Proof of Payment</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div className="bg-slate-50 rounded-lg p-4 space-y-2">
                <div className="flex justify-between text-sm"><span className="text-slate-500">Reference</span><span className="font-mono font-medium">{proofDialog?.reference}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Amount</span><span className="font-heading text-xl font-bold text-slate-900">${proofDialog?.amount?.toFixed(2)}</span></div>
              </div>

              <div className="border-2 border-dashed border-slate-200 rounded-xl p-6 text-center hover:border-sky-300 transition-colors">
                <Upload className="h-8 w-8 text-slate-400 mx-auto mb-3" />
                <p className="text-sm font-medium text-slate-700 mb-1">Upload receipt or proof of payment</p>
                <p className="text-xs text-slate-400 mb-3">PDF, JPG, or PNG (max 10MB)</p>
                <input
                  type="file"
                  ref={fileRef}
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={handleProofUpload}
                  className="hidden"
                  data-testid="proof-file-input"
                />
                <Button
                  variant="outline"
                  onClick={() => fileRef.current?.click()}
                  disabled={uploading}
                  data-testid="select-proof-file-btn"
                >
                  {uploading ? <><Loader2 className="h-4 w-4 animate-spin mr-1.5" /> Uploading...</> : <><FileUp className="h-4 w-4 mr-1.5" /> Choose File</>}
                </Button>
              </div>

              <p className="text-[11px] text-slate-400 text-center leading-relaxed">
                Your proof of payment will be reviewed by our finance team. Once verified, your account will be credited.
              </p>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
